import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  collection, onSnapshot, deleteDoc, doc,
  getDocs, orderBy, query, writeBatch
} from 'firebase/firestore';

const ADMIN_UID = '2FE2mdzEuQTbNcpgn9Uxq74Aeiv2';

const fmt = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' });
};

// ── Modal אישור מחיקה ──
const ConfirmModal = ({ onConfirm, onCancel, count }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24,
  }}>
    <div style={{
      background: '#0f172a', border: '1px solid #334155',
      borderRadius: 14, padding: '28px 32px', maxWidth: 380, width: '100%',
      direction: 'rtl', boxShadow: '0 25px 50px rgba(0,0,0,0.6)',
    }}>
      {/* Icon */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%',
          background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
          margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22,
        }}>🗑️</div>
      </div>
      {/* Title */}
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <div style={{ color: '#f1f5f9', fontWeight: 800, fontSize: 16 }}>מחיקת כל הלוגים</div>
      </div>
      {/* Body */}
      <div style={{
        textAlign: 'center', color: '#94a3b8', fontSize: 13, marginBottom: 24, lineHeight: 1.6,
      }}>
        פעולה זו תמחק <span style={{ color: '#ef4444', fontWeight: 700, fontFamily: 'monospace' }}>{count}</span> לוגים לצמיתות.
        <br />לא ניתן לשחזר אותם.
      </div>
      {/* Buttons */}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onCancel} style={{
          flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer',
          background: '#1e293b', border: '1px solid #334155',
          color: '#94a3b8', fontWeight: 600, fontSize: 13,
        }}>ביטול</button>
        <button onClick={onConfirm} style={{
          flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer',
          background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)',
          color: '#ef4444', fontWeight: 700, fontSize: 13,
        }}>מחק הכל</button>
      </div>
    </div>
  </div>
);

const logMeta = (log) => {
  if (log.event === 'rate_limit_hit') return { dot: '#f97316', tag: '429',      tagBg: 'rgba(249,115,22,0.15)',  tagColor: '#f97316' };
  if (log.level === 'error')          return { dot: '#ef4444', tag: 'ERROR',    tagBg: 'rgba(239,68,68,0.12)',   tagColor: '#ef4444' };
  if (log.event === 'user_login')     return { dot: '#22c55e', tag: 'LOGIN',    tagBg: 'rgba(34,197,94,0.12)',   tagColor: '#22c55e' };
  return                                     { dot: '#3b82f6', tag: 'EXTRACT',  tagBg: 'rgba(59,130,246,0.12)',  tagColor: '#3b82f6' };
};

// ── Compact Stat Card ──
const Stat = ({ label, value, color }) => (
  <div style={{
    background: '#0f172a', border: `1px solid ${color}22`,
    borderRadius: 10, padding: '12px 16px', flex: 1, minWidth: 110,
    borderTop: `2px solid ${color}`,
  }}>
    <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: 'monospace', lineHeight: 1 }}>{value}</div>
    <div style={{ color: '#64748b', fontSize: 11, marginTop: 4, fontWeight: 600 }}>{label}</div>
  </div>
);

// ── Single Log Row ──
const LogRow = ({ log, onDelete }) => {
  const m = logMeta(log);
  const [open, setOpen] = useState(false);

  const mainText = () => {
    if (log.event === 'user_login')     return log.userDetails?.name ? `${log.userDetails.name} — ${log.userDetails.email}` : log.userEmail;
    if (log.event === 'recipe_extracted') return `${log.platform} · ${log.extractionMethod} · ${log.userEmail}`;
    if (log.event === 'rate_limit_hit') return `${log.userEmail} · ${log.payload || ''}`;
    return log.message || log.event;
  };

  return (
    <div style={{
      background: '#0c1220',
      border: '1px solid #1e293b',
      borderRight: `3px solid ${m.dot}`,
      borderRadius: 8, marginBottom: 6,
      overflow: 'hidden',
    }}>
      {/* Main row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 14px', cursor: log.stack ? 'pointer' : 'default'
      }} onClick={() => log.stack && setOpen(o => !o)}>

        {/* Tag */}
        <span style={{
          background: m.tagBg, color: m.tagColor,
          fontSize: 10, fontWeight: 800, fontFamily: 'monospace',
          padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap', letterSpacing: 1,
        }}>{m.tag}</span>

        {/* Text */}
        <span style={{
          color: '#cbd5e1', fontSize: 12, fontFamily: 'monospace',
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{mainText()}</span>

        {/* Timestamp */}
        <span style={{ color: '#475569', fontSize: 11, whiteSpace: 'nowrap' }}>{fmt(log.timestamp)}</span>

        {/* Stack indicator */}
        {log.stack && (
          <span style={{ color: '#f59e0b', fontSize: 11 }}>{open ? '▲' : '▼'}</span>
        )}

        {/* Delete */}
        <button
          onClick={e => { e.stopPropagation(); onDelete(log.id); }}
          style={{
            background: 'transparent', border: 'none',
            color: '#475569', cursor: 'pointer', fontSize: 14, padding: '0 2px',
            lineHeight: 1, borderRadius: 4,
          }}
          title="מחק"
        >×</button>
      </div>

      {/* Extra details for login */}
      {log.event === 'user_login' && log.userDetails && (
        <div style={{
          padding: '0 14px 9px', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {log.userDetails.photoURL && (
            <img src={log.userDetails.photoURL} alt="" style={{ width: 20, height: 20, borderRadius: '50%' }} />
          )}
          <span style={{ color: '#64748b', fontSize: 11, fontFamily: 'monospace' }}>
            uid: {log.userDetails.uid}
          </span>
        </div>
      )}

      {/* Stack trace */}
      {open && log.stack && (
        <pre style={{
          margin: 0, padding: '10px 14px',
          background: '#060d1a', color: '#a3e635',
          fontSize: 11, fontFamily: 'monospace',
          borderTop: '1px solid #1e293b',
          overflowX: 'auto', maxHeight: 180, overflowY: 'auto',
        }}>{log.stack}</pre>
      )}

      {/* 429 badge */}
      {log.event === 'rate_limit_hit' && (
        <div style={{ padding: '0 14px 9px' }}>
          <span style={{
            background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)',
            color: '#fb923c', fontSize: 10, fontFamily: 'monospace', fontWeight: 700,
            padding: '2px 8px', borderRadius: 4,
          }}>HTTP 429 — Too Many Requests</span>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════
const AdminScreen = () => {
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();

  const [logs, setLogs]           = useState([]);
  const [filter, setFilter]       = useState('all');
  const [search, setSearch]       = useState('');
  const [clearing, setClearing]   = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastSync, setLastSync]   = useState(null);
  const [stats, setStats]         = useState({ logins: 0, errors: 0, rate: 0, extracts: 0 });

  useEffect(() => {
    if (!loading && (!user || user.uid !== ADMIN_UID)) navigate('/');
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user || user.uid !== ADMIN_UID) return;
    const q = query(collection(db, 'logs'), orderBy('timestamp', 'desc'));
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLogs(data);
      setLastSync(new Date());

      const now = new Date();
      const day = new Date(now - 86400000);
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      setStats({
        logins:   data.filter(l => l.event === 'user_login'     && l.timestamp?.toDate?.() >= today).length,
        errors:   data.filter(l => l.level === 'error'          && l.timestamp?.toDate?.() >= day).length,
        rate:     data.filter(l => l.event === 'rate_limit_hit' && l.timestamp?.toDate?.() >= day).length,
        extracts: data.filter(l => l.event === 'recipe_extracted').length,
      });
    });
    return () => unsub();
  }, [user, refreshKey]);

  const handleDelete = async id => {
    try { await deleteDoc(doc(db, 'logs', id)); } catch {}
  };

  const handleClearAll = async () => {
    setShowConfirm(false);
    setClearing(true);
    try {
      const snap = await getDocs(collection(db, 'logs'));
      const batch = writeBatch(db);
      snap.docs.forEach(d => batch.delete(d.ref));
      await batch.commit();
    } catch {}
    finally { setClearing(false); }
  };

  const FILTERS = [
    { key: 'all',       label: 'הכל' },
    { key: 'error',     label: 'Errors' },
    { key: 'ratelimit', label: '429' },
    { key: 'login',     label: 'Logins' },
    { key: 'extract',   label: 'Extracts' },
  ];

  const filtered = logs.filter(log => {
    const ok = filter === 'all'
      || (filter === 'error'     && log.level === 'error')
      || (filter === 'ratelimit' && log.event === 'rate_limit_hit')
      || (filter === 'login'     && log.event === 'user_login')
      || (filter === 'extract'   && log.event === 'recipe_extracted');
    const s = search.toLowerCase();
    const match = !s
      || log.userEmail?.toLowerCase().includes(s)
      || log.message?.toLowerCase().includes(s)
      || log.payload?.toLowerCase().includes(s)
      || log.userDetails?.email?.toLowerCase().includes(s)
      || log.userDetails?.name?.toLowerCase().includes(s);
    return ok && match;
  });

  if (loading || !user || user.uid !== ADMIN_UID) return null;

  return (
    <div style={{ minHeight: '100vh', background: '#020817', color: '#e2e8f0', fontFamily: 'system-ui, sans-serif', direction: 'rtl' }}>

      {/* ── Confirm Modal ── */}
      {showConfirm && (
        <ConfirmModal
          count={logs.length}
          onConfirm={handleClearAll}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* ── HEADER ── */}
      <div style={{
        background: '#0a0f1c', borderBottom: '1px solid #1e293b',
        padding: '0 24px', height: 56,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        {/* Left: title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16 }}>🛡️</span>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9' }}>Admin</span>
          <span style={{ color: '#334155', fontSize: 14 }}>/</span>
          <span style={{ color: '#64748b', fontSize: 13 }}>CookWithAI</span>
          {lastSync && (
            <span style={{ color: '#334155', fontSize: 11, fontFamily: 'monospace' }}>
              · synced {lastSync.toLocaleTimeString('he-IL', { timeStyle: 'short' })}
            </span>
          )}
        </div>

        {/* Right: actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setRefreshKey(k => k + 1)} style={btnStyle('#1e293b', '#94a3b8')}>
            🔄
          </button>
          <button onClick={() => setShowConfirm(true)} disabled={clearing} style={btnStyle('rgba(239,68,68,0.1)', '#ef4444', '1px solid rgba(239,68,68,0.25)')}>
            {clearing ? '...' : '🗑️ נקה'}
          </button>
          <button onClick={() => navigate('/')} style={btnStyle('#1e293b', '#64748b')}>
            ← יציאה
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px' }}>

        {/* ── STATS ROW ── */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
          <Stat label="כניסות היום"    value={stats.logins}   color="#22c55e" />
          <Stat label="שגיאות (24h)"   value={stats.errors}   color="#ef4444" />
          <Stat label="429 (24h)"       value={stats.rate}     color="#f97316" />
          <Stat label="חילוצים הכל"    value={stats.extracts} color="#f59e0b" />
          <Stat label="סה״כ לוגים"     value={logs.length}    color="#3b82f6" />
        </div>

        {/* ── FILTERS + SEARCH ── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden' }}>
            {FILTERS.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} style={{
                background: filter === f.key ? '#1e40af' : 'transparent',
                color: filter === f.key ? '#fff' : '#64748b',
                border: 'none', padding: '7px 14px',
                cursor: 'pointer', fontSize: 12, fontWeight: 600,
                transition: 'all 0.15s',
              }}>{f.label}</button>
            ))}
          </div>

          <input
            placeholder="חפש: email, message, payload..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              flex: 1, minWidth: 180, background: '#0f172a',
              border: '1px solid #1e293b', color: '#e2e8f0',
              borderRadius: 8, padding: '7px 12px', fontSize: 12,
              outline: 'none', direction: 'rtl', fontFamily: 'monospace',
            }}
          />

          <span style={{ color: '#334155', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
            {filtered.length}/{logs.length}
          </span>
        </div>

        {/* ── LOG LIST ── */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#334155' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
            <div style={{ fontSize: 13 }}>אין לוגים</div>
          </div>
        ) : (
          filtered.map(log => <LogRow key={log.id} log={log} onDelete={handleDelete} />)
        )}
      </div>
    </div>
  );
};

// ── Button style helper ──
const btnStyle = (bg, color, border = '1px solid #334155') => ({
  background: bg, border, color,
  borderRadius: 6, padding: '6px 12px',
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
  whiteSpace: 'nowrap',
});

export default AdminScreen;
